import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAdminBusiness } from '../../contexts/AdminBusinessContext'
import { useTheme } from '../../contexts/ThemeContext'
import { ordersAPI, menuAPI, tablesAPI, paymentsAPI } from '../../services/api'
import toast from 'react-hot-toast'
import { printBill, loadBusinessInfo } from '../../utils/bill'
import CurrencyCode from '../../components/CurrencyCode'
import LoyaltyPayModal from '../../components/LoyaltyPayModal'
import {
  ShoppingCartIcon,
  PrinterIcon,
  TrashIcon,
  PlusIcon,
  MinusIcon,
  XMarkIcon,
  CreditCardIcon,
  PencilIcon
} from '@heroicons/react/24/outline'
import { t } from '../../i18n'

function POSPage() {
  const { businessType, branch, subscriptionLocked } = useAdminBusiness()
  const { getCurrency } = useTheme()
  const isRestaurant = businessType === 'restaurant'
  const [orderType, setOrderType] = useState(isRestaurant ? 'DINE_IN' : 'TAKE_OUT')
  const [selectedTable, setSelectedTable] = useState(null)
  const [tables, setTables] = useState([])
  const [deliveryAddress, setDeliveryAddress] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerName, setCustomerName] = useState('')
  
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [cart, setCart] = useState([])
  
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [showPayment, setShowPayment] = useState(false)
  const [loading, setLoading] = useState(false)
  const [processingCard, setProcessingCard] = useState(false)
  
  const [selectedItem, setSelectedItem] = useState(null)
  const [showItemModal, setShowItemModal] = useState(false)
  const [quantity, setQuantity] = useState(1)
  // Chosen options for the item in the modal: one, or several when allowed.
  const [selectedVariants, setSelectedVariants] = useState([])
  const toggleVariant = variant => setSelectedVariants(prev => {
    const picked = prev.some(v => v.id === variant.id)
    if (selectedItem?.variant_selection === 'multiple') return picked ? prev.filter(v => v.id !== variant.id) : [...prev, variant]
    return picked ? [] : [variant]
  })
  const [note, setNote] = useState('')
  
  const [completedOrder, setCompletedOrder] = useState(null)
  const [showSubscriptionLock, setShowSubscriptionLock] = useState(false)

  const [showLoyaltyModal, setShowLoyaltyModal] = useState(false)
  const [loyaltyCustomer, setLoyaltyCustomer] = useState(null)
  const [loyaltyReward, setLoyaltyReward] = useState(null)
  // Store name, contact details and currency for the printed bill.
  const [business, setBusiness] = useState(null)
  useEffect(() => { loadBusinessInfo(getCurrency()).then(setBusiness) }, [])

  useEffect(() => {
    if (isRestaurant) loadTables()
    loadMenu()
  }, [])

  const loadTables = async () => {
    try {
      const response = await tablesAPI.getTables()
      setTables(response.data.tables || [])
    } catch (error) {
      console.error('Failed to load tables:', error)
    }
  }

  const loadMenu = async () => {
    try {
      const branchId = branch.id
      const response = await menuAPI.getMenu({ branchId, catalog: 'all' })
      console.log('Menu response:', response.data)
      setCategories(response.data.categories || [])
      if (response.data.categories?.length > 0) {
        setSelectedCategory(response.data.categories[0].id)
      }
    } catch (error) {
      console.error('Failed to load menu:', error)
      toast.error(t("Failed to load menu"))
    }
  }

  const getTableStatus = (table) => {
    const hasActiveOrder = table.activeOrder !== undefined && table.activeOrder !== null 
    return hasActiveOrder ? 'busy' : 'free'
  }

  const updateTableStatus = (tableId, isBusy) => {
    setTables(prevTables => 
      prevTables.map(table => 
        table.id === tableId 
          ? { ...table, activeOrder: isBusy }
          : table
      )
    )
  }

  const getCurrentItems = () => {
    const category = categories.find(c => c.id === selectedCategory)
    return category?.items || []
  }

  const handleAddItemClick = (item) => {
    // Check if item has variants
    const hasVariants = item.variants && item.variants.length > 0
    
    if (hasVariants || item.modifiers?.length > 0) {
      // Show modal for items with variants or modifiers
      setSelectedItem(item)
      setQuantity(1)
      setSelectedVariants([])
      setNote('')
      setShowItemModal(true)
    } else {
      // Direct add to cart for items without variants or modifiers
      addToCart(item, null, '')
    }
  }


    const playBeep = () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.type = "square";        // clicky sharp sound
    oscillator.frequency.setValueAtTime(500, ctx.currentTime); // Hz
    gain.gain.setValueAtTime(0.2, ctx.currentTime); // volume

    oscillator.connect(gain);
    gain.connect(ctx.destination);

    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.05); // very short beep
  };



  const optionFields = (basePrice, variants) => ({
    variant: variants.map(v => v.name).join(', ') || null,
    variantId: variants[0]?.id,
    variantIds: variants.map(v => v.id),
    variantKey: variants.map(v => v.id).sort((a, b) => a - b).join(','),
    price: basePrice + variants.reduce((sum, v) => sum + parseFloat(v.price_adjustment || 0), 0),
  })

  const addToCart = (item, variants = [], note = '') => {
    const cartItem = {
      id: `${item.id}-${variants.map(v => v.id).join('.') || 'base'}-${Date.now()}`,
      menuItemId: item.id,
      name: item.name,
      // Kept so the line can be edited later from the cart.
      basePrice: parseFloat(item.price),
      variants: item.variants || [],
      variant_selection: item.variant_selection,
      ...optionFields(parseFloat(item.price), variants),
      quantity: 1,
      note: note,
      modifiers: [] // You can extend this to include modifiers if needed
    }
  playBeep();
    setCart(prev => {
      const existing = prev.find(i => 
        i.menuItemId === cartItem.menuItemId && 
        i.variantKey === cartItem.variantKey && 
        i.note === cartItem.note
      )
      if (existing) {
        return prev.map(i => 
          i.id === existing.id 
            ? { ...i, quantity: i.quantity + 1 }
            : i
        )
      }
      return [...prev, cartItem]
    })
    toast.success(t("Added to cart"))
  }

  const handleAddWithOptions = () => {
    if (!selectedItem) return

    addToCart(selectedItem, selectedVariants, note)
    
    // Reset modal
    setShowItemModal(false)
    setSelectedItem(null)
    setQuantity(1)
    setSelectedVariants([])
    setNote('')
  }

  const updateQuantity = (cartItemId, change) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.id === cartItemId) {
          const newQty = item.quantity + change
          return newQty > 0 ? { ...item, quantity: newQty } : item
        }
        return item
      }).filter(item => item.quantity > 0)
    })
  }

  const removeFromCart = (cartItemId) => {
    setCart(prev => prev.filter(item => item.id !== cartItemId))
    toast.success(t("Item removed"))
  }

  const editCartItem = (item) => {
    setSelectedItem(item)
    setQuantity(item.quantity)
    setSelectedVariants((item.variants || []).filter(v => (item.variantIds || []).includes(v.id)))
    setNote(item.note || '')
    setShowItemModal(true)
  }

  const updateCartItem = () => {
    if (!selectedItem) return

    setCart(prev => prev.map(item => {
      if (item.id === selectedItem.id) {
        return {
          ...item,
          ...optionFields(item.basePrice ?? parseFloat(item.price), selectedVariants),
          quantity: quantity,
          note: note
        }
      }
      return item
    }))

    setShowItemModal(false)
    setSelectedItem(null)
    setQuantity(1)
    setSelectedVariants([])
    setNote('')
    toast.success(t("Item updated"))
  }

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  }

  // Preview only - the server independently validates and computes the real
  // discount when the order is created, so a stale/tampered value here can't
  // actually change what the customer is charged.
  const getLoyaltyDiscount = () => {
    if (!loyaltyReward) return 0
    const subtotal = getCartTotal()
    if (loyaltyReward.type === 'percent_off') return subtotal * (loyaltyReward.value / 100)
    if (loyaltyReward.type === 'amount_off') return Math.min(loyaltyReward.value, subtotal)
    if (loyaltyReward.type === 'free_item') return 0 // item price varies; server applies it exactly
    return 0
  }

  // Same rule as the server: tax on the items, service charge on dine-in only.
  const getTax = () => getCartTotal() * (Number(business?.taxRate) || 0) / 100
  const getServiceCharge = () => (orderType === 'DINE_IN' ? getCartTotal() * (Number(business?.serviceCharge) || 0) / 100 : 0)
  const getFinalTotal = () => Math.max(0, getCartTotal() + getTax() + getServiceCharge() - getLoyaltyDiscount())

  const getChange = () => {
    const paid = parseFloat(paymentAmount) || 0
    return paid - getFinalTotal()
  }

  const validateOrder = () => {
    if (cart.length === 0) {
      toast.error(t("Cart is empty"))
      return false
    }

    if (orderType === 'DINE_IN' && !selectedTable) {
      toast.error(t("Please select a table"))
      return false
    }

    return true
  }

  const simulateCardPayment = async () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const success = Math.random() > 0.1
        resolve(success)
      }, 2000)
    })
  }

  const handleSubmitOrder = async () => {
    if (subscriptionLocked) {
      setShowPayment(false)
      setShowSubscriptionLock(true)
      return
    }
    if (!validateOrder()) return

    if (paymentMethod === 'cash' && (!paymentAmount || parseFloat(paymentAmount) < getFinalTotal())) {
      toast.error(t("Payment amount must be at least the order total"))
      return
    }

    try {
      setLoading(true)

      const orderData = {
        // branchId intentionally omitted - the server derives it from the
        // logged-in cashier's own branch, which can't be spoofed.
        orderType: orderType,
        source: 'pos',
        tableId: orderType === 'DINE_IN' && selectedTable ? selectedTable.id : null,
        tableNumber: orderType === 'DINE_IN' && selectedTable ? selectedTable.table_number : null,
        customerName: customerName || 'Guest',
        customerPhone: customerPhone || null,
        deliveryAddress: deliveryAddress || null,
        paymentMethod: paymentMethod,
        paymentStatus:"PAID",
        orderStatus: "PREPARING",
        customerId: loyaltyCustomer?.id || null,
        rewardId: loyaltyReward?.id || null,
        amountPaid: paymentMethod === 'cash' ? parseFloat(paymentAmount) : getFinalTotal(),
        changeAmount: paymentMethod === 'cash' ? getChange() : 0,
        items: cart.map(item => ({
          menuItemId: item.menuItemId,
          variantId: item.variantId,
          variantIds: item.variantIds || [],
          variantName: item.variant,
          quantity: item.quantity,
          unitPrice: item.price,
          note: item.note
        }))
      }

      let paymentSuccessful = true
      
      if (paymentMethod === 'card') {
        paymentSuccessful = await simulateCardPayment()
        
        if (!paymentSuccessful) {
          toast.error(t("Card payment failed. Please try again or use cash."))
          return
        }
      }

      const response = await ordersAPI.createOrder(orderData)
      
      if (orderType === 'DINE_IN' && selectedTable) {
        updateTableStatus(selectedTable.id, true)
      }
      
      const data = response.data || {}
      const sale = {
        ...data,
        order_code: data.orderCode,
        table_number: data.tableNumber,
        customer_name: customerName || '',
        payment_method: paymentMethod,
        items: cart,
        amountPaid: paymentMethod === 'cash' ? parseFloat(paymentAmount) : Number(data.total ?? getFinalTotal()),
        change: paymentMethod === 'cash' ? getChange() : 0,
      }
      setCompletedOrder(sale)

      toast.success(t("Order completed successfully! {v0}", { v0: paymentMethod === 'card' ? 'Card payment processed.' : '' }))

      // Reset form
      setCart([])
      setPaymentAmount('')
      setShowPayment(false)
      setCustomerName('')
      setCustomerPhone('')
      setDeliveryAddress('')
      setLoyaltyCustomer(null)
      setLoyaltyReward(null)
      if (orderType === 'DINE_IN') {
        setSelectedTable(null)
      }
      
      printBill(sale, business || { currency: getCurrency() }, { amountPaid: sale.amountPaid, change: sale.change })
      
    } catch (error) {
      console.error('Order submission error:', error)
      toast.error(t(error.response?.data?.error || 'Failed to submit order'))
    } finally {
      setLoading(false)
    }
  }

  const reprintLast = () => {
    if (completedOrder) printBill(completedOrder, business || { currency: getCurrency() }, { amountPaid: completedOrder.amountPaid, change: completedOrder.change })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showSubscriptionLock && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="pos-subscription-title">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl sm:p-8">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-red-50 text-red-600"><CreditCardIcon className="h-7 w-7" /></span>
            <h2 id="pos-subscription-title" className="mt-5 text-2xl font-bold text-gray-950">{t('Your subscription is inactive')}</h2>
            <p className="mt-3 leading-7 text-gray-600">{t('Sales are paused until you pay for your plan. Your cart is saved.')}</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={() => setShowSubscriptionLock(false)} className="btn-secondary min-h-11">{t('Back to POS')}</button>
              <Link to="/admin/billing" className="btn-primary min-h-11 inline-flex items-center justify-center gap-2"><CreditCardIcon className="h-5 w-5" />{t('Pay subscription')}</Link>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 py-6">
        
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t("Point of Sale")}</h1>
            <p className="text-gray-600">{t("Complete POS System")}</p>
          </div>
          {completedOrder && (
            <button type="button" onClick={reprintLast} className="btn-secondary inline-flex items-center gap-2">
              <PrinterIcon className="h-5 w-5" />
              {t("Print last bill")} #{completedOrder.order_code}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          <div className="lg:col-span-2 space-y-6">
            
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">{t("Order Type")}</h2>
              <div className="grid grid-cols-3 gap-3">
                {isRestaurant && <button
                  onClick={() => setOrderType('DINE_IN')}
                  className={`py-3 px-4 rounded-lg font-medium transition ${
                    orderType === 'DINE_IN'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t("Dine In")}
                </button>}
                <button
                  onClick={() => setOrderType('DELIVERY')}
                  className={`py-3 px-4 rounded-lg font-medium transition ${
                    orderType === 'DELIVERY'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t("Delivery")}
                </button>
                <button
                  onClick={() => setOrderType('TAKE_OUT')}
                  className={`py-3 px-4 rounded-lg font-medium transition ${
                    orderType === 'TAKE_OUT'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {isRestaurant ? t("Take Out") : t("In-store / Pickup")}
                </button>
              </div>

              {orderType === 'DINE_IN' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("Select Table")}
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {tables.map(table => {
                      const status = getTableStatus(table)
                      const isBusy = status === 'busy'
                      return (
                        <button
                          key={table.id}
                          onClick={() =>  setSelectedTable(table)}
                          className={`py-2 px-3 rounded-lg font-medium transition relative ${
                            selectedTable?.id === table.id
                              ? 'bg-green-600 text-white'
                              : isBusy
                              ? 'bg-red-100 text-red-700 '
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {table.table_number}
                          <span className={`absolute -top-1 -end-1 w-3 h-3 rounded-full ${
                            isBusy ? 'bg-red-500' : 'bg-green-500'
                          }`}></span>
                          {isBusy && (
                            <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 bg-red-500 text-white text-xs px-1 rounded">
                              {t("Busy")}
                            </span>
                          )}
                          {selectedTable?.id === table.id && !isBusy && (
                            <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 bg-green-500 text-white text-xs px-1 rounded">
                              {t("Selected")}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {orderType === 'DELIVERY' && (
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Customer Name (Optional)")}
                    </label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder={t("Enter customer name")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Phone Number (Optional)")}
                    </label>
                    <input
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder={t("Enter phone number")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Delivery Address (Optional)")}
                    </label>
                    <textarea
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows="2"
                      placeholder={t("Enter delivery address")}
                    />
                  </div>
                </div>
              )}

              {orderType === 'TAKE_OUT' && (
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Customer Name (Optional)")}
                    </label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder={t("Enter customer name")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Phone Number (Optional)")}
                    </label>
                    <input
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder={t("Enter phone number")}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">{t("Menu")}</h2>
              
              <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap transition ${
                      selectedCategory === category.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {getCurrentItems().map(item => (
                  <MenuItemCard
                    key={item.id}
                    item={item}
                    onAddToCart={handleAddItemClick}
                  />
                ))}
              </div>

              {getCurrentItems().length === 0 && (
                <p className="text-center text-gray-500 py-8">{t("No items available in this category")}</p>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <ShoppingCartIcon className="w-6 h-6" />
                  {t("Cart")}
                </h2>
                <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                  {cart.length} {t("items")}
                </span>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
                {cart.map(item => (
                  <div key={item.id} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.name}</p>
                        {item.variant && (
                          <p className="text-xs text-gray-600">{t("Option:")} {item.variant}</p>
                        )}
                        {item.note && (
                          <p className="text-xs text-gray-600 mt-1">{t("Note:")} {item.note}</p>
                        )}
                        <p className="text-sm text-gray-600">{item.price.toFixed(2)} <CurrencyCode /> {t("each")}</p>
                      </div>
                      <button
                        onClick={() => editCartItem(item)}
                        className="p-1 hover:bg-gray-200 rounded ms-2"
                      >
                        <PencilIcon className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <MinusIcon className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <PlusIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="p-1 hover:bg-red-100 text-red-600 rounded"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {cart.length === 0 && (
                  <p className="text-center text-gray-500 py-8">{t("Cart is empty")}</p>
                )}
              </div>

              <div className="border-t pt-4 space-y-2">
                {(loyaltyReward || getTax() > 0 || getServiceCharge() > 0) && (
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>{t("Subtotal:")}</span>
                    <span>{getCartTotal().toFixed(2)} <CurrencyCode /></span>
                  </div>
                )}
                {getTax() > 0 && (
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>{t("Tax")} ({business.taxRate}%)</span>
                    <span>{getTax().toFixed(2)} <CurrencyCode /></span>
                  </div>
                )}
                {getServiceCharge() > 0 && (
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>{t("Service charge")} ({business.serviceCharge}%)</span>
                    <span>{getServiceCharge().toFixed(2)} <CurrencyCode /></span>
                  </div>
                )}
                {loyaltyReward && (
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>🎁 {loyaltyReward.name}:</span>
                    <span>-{getLoyaltyDiscount().toFixed(2)} <CurrencyCode /></span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-semibold">
                  <span>{t("Total:")}</span>
                  <span>{getFinalTotal().toFixed(2)} <CurrencyCode /></span>
                </div>

                <button
                  type="button"
                  onClick={() => setShowLoyaltyModal(true)}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-1"
                >
                  {loyaltyCustomer
                    ? t("🎁 {v0} • {points_balance} pts{v2} (change)", { v0: loyaltyCustomer.name || loyaltyCustomer.phone, points_balance: loyaltyCustomer.points_balance, v2: loyaltyReward ? ' • reward applied' : '' })
                    : t("+ Add loyalty customer")}
                </button>

                {showPayment && (
                  <div className="space-y-3 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("Payment Method")}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setPaymentMethod('cash')}
                          className={`py-2 px-4 rounded-lg font-medium transition ${
                            paymentMethod === 'cash'
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {t("💵 Cash")}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentMethod('card')}
                          className={`py-2 px-4 rounded-lg font-medium transition flex items-center justify-center gap-1 ${
                            paymentMethod === 'card'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <CreditCardIcon className="w-4 h-4" />
                          {t("Card")}
                        </button>
                      </div>
                    </div>
                    
                    {paymentMethod === 'cash' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("Amount Paid")}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-lg"
                          placeholder="0.00"
                          autoFocus
                        />
                      </div>
                    )}
                    
                    {paymentMethod === 'cash' && paymentAmount && parseFloat(paymentAmount) >= getFinalTotal() && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex justify-between text-lg">
                          <span className="font-medium">{t("Change:")}</span>
                          <span className="font-bold text-green-600">
                            {getChange().toFixed(2)} <CurrencyCode />
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={() => {
                    if (!showPayment) {
                      setShowLoyaltyModal(true)
                    } else {
                      handleSubmitOrder()
                    }
                  }}
                  disabled={cart.length === 0 || loading || processingCard}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                >
                  {processingCard ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      {t("Processing Card...")}
                    </>
                  ) : loading ? (
                    t("Processing...")
                  ) : showPayment ? (
                    paymentMethod === 'card' ? t("Process Card Payment") : t("Complete Order")
                  ) : (
                    t("Proceed to Payment")
                  )}
                </button>

                {showPayment && (
                  <button
                    onClick={() => {
                      setShowPayment(false)
                      setPaymentAmount('')
                      setPaymentMethod('cash')
                    }}
                    className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-200 transition"
                  >
                    {t("Cancel Payment")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Item Modal for Variants and Notes */}
      {showItemModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedItem.name}
                </h3>
                <button
                  onClick={() => setShowItemModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {selectedItem.description && (
                <p className="text-gray-600 mb-4 text-sm">{t(selectedItem.description)}</p>
              )}

              {/* Variants Section */}
              {selectedItem.variants && selectedItem.variants.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">{t("Options (Optional)")}</h4>
                  <p className="text-xs text-gray-500 mb-2">{selectedItem.variant_selection === 'multiple' ? t("Choose any options you like, or skip them") : t("Choose one option, or skip to order without options")}</p>
                  <div className="space-y-2">
                    {selectedItem.variants.map((variant) => (
                      <label key={variant.id} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedVariants.some(v => v.id === variant.id)}
                          onChange={() => toggleVariant(variant)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                        />
                        <span className="ms-2 text-sm text-gray-700 flex-1">
                          {variant.name}
                          {parseFloat(variant.price_adjustment || 0) !== 0 && (
                            <span className={`ms-1 ${variant.price_adjustment > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {variant.price_adjustment > 0 ? '+' : ''}
                              {parseFloat(variant.price_adjustment || 0).toFixed(2)} {getCurrency()}
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Note Section */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Special Instructions")}
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("Any special requests?")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows={3}
                />
              </div>

              {/* Quantity Section */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Quantity")}
                </label>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    disabled={quantity <= 1}
                  >
                    <MinusIcon className="h-4 w-4" />
                  </button>
                  <span className="text-lg font-medium w-8 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowItemModal(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={selectedItem.id in cart ? updateCartItem : handleAddWithOptions}
                  className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  {selectedItem.id in cart ? t("Update Item") : t("Add to Cart")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {showLoyaltyModal && (
        <LoyaltyPayModal
          onClose={() => setShowLoyaltyModal(false)}
          onDone={(result) => {
            setLoyaltyCustomer(result?.customer || null)
            setLoyaltyReward(result?.reward || null)
            setShowLoyaltyModal(false)
            setShowPayment(true)
          }}
        />
      )}
    </div>
  )
}

function MenuItemCard({ item, onAddToCart }) {
  const hasVariants = item.variants && item.variants.length > 0
  const hasModifiers = item.modifiers && item.modifiers.length > 0

  return (
    <div className="border rounded-lg overflow-hidden hover:shadow-lg transition">
      {item.image && (
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-32 object-cover"
        />
      )}
      <div className="p-3">
        <h3 className="font-semibold text-sm mb-1">{item.name}</h3>
        <p className="text-xs text-gray-600 mb-2 line-clamp-2">{t(item.description)}</p>
        <p className="text-blue-600 font-bold mb-2">{parseFloat(item.price).toFixed(2)} <CurrencyCode /></p>
        
        <button
          onClick={() => onAddToCart(item)}
          className="w-full bg-blue-600 text-white py-2 rounded text-sm font-medium hover:bg-blue-700 transition"
        >
          {hasVariants || hasModifiers ? t("Select Options") : t("Add to Cart")}
        </button>
      </div>
    </div>
  )
}

export default POSPage
